<template>
  <div class="enter-result">
    <ScoreBar />

    <div class="content">
      <!-- Contract summary -->
      <div v-if="contractState" class="contract-card">
        <span class="contract-name">{{ contractState.label }}</span>
        <div v-if="contractState.suitSymbol || contractState.tricks" class="contract-details">
          <span v-if="contractState.suitSymbol" class="contract-suit" :class="{ red: contractState.suitRed }">
            {{ contractState.suitSymbol }}
          </span>
          <span v-if="contractState.tricks" class="contract-tricks">{{ contractState.tricks }} slagen</span>
        </div>
        <div class="contract-players">
          <span
            v-for="id in contractState.playerIds"
            :key="id"
            class="contract-player"
          >{{ playerName(id) }}</span>
        </div>
      </div>

      <!-- Tricks-based result (samen, alleen, abondance) -->
      <section v-if="resultMode === 'tricks'" class="section">
        <h2 class="section-title">Behaalde slagen</h2>
        <div class="tricks-grid">
          <button
            v-for="n in 14"
            :key="n - 1"
            class="tricks-chip"
            :class="{ selected: tricksTaken === (n - 1) }"
            @click="tricksTaken = n - 1"
          >
            {{ n - 1 }}
          </button>
        </div>
      </section>

      <!-- Pass/fail per player (kleine-miserie, piccolo, grote-miserie, open-miserie) -->
      <section v-if="resultMode === 'per-player'" class="section">
        <h2 class="section-title">Resultaat per speler</h2>
        <div class="player-results">
          <div
            v-for="id in contractState.playerIds"
            :key="id"
            class="player-result-row"
          >
            <span class="player-result-name">{{ playerName(id) }}</span>
            <div class="toggle-group">
              <button
                class="toggle-btn succeeded"
                :class="{ active: playerResults[id] === true }"
                @click="setPlayerResult(id, true)"
              >
                Geslaagd
              </button>
              <button
                class="toggle-btn failed"
                :class="{ active: playerResults[id] === false }"
                @click="setPlayerResult(id, false)"
              >
                Mislukt
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Pass/fail single (troel, solo-slim) -->
      <section v-if="resultMode === 'single'" class="section">
        <h2 class="section-title">Resultaat</h2>
        <div class="single-result">
          <button
            class="result-btn succeeded"
            :class="{ active: singleResult === true }"
            @click="singleResult = true"
          >
            Geslaagd
          </button>
          <button
            class="result-btn failed"
            :class="{ active: singleResult === false }"
            @click="singleResult = false"
          >
            Mislukt
          </button>
        </div>
      </section>

      <!-- Score preview — always rendered to prevent layout shift -->
      <section class="section">
        <h2 class="section-title">Punten</h2>
        <div class="score-preview">
          <div
            v-for="player in players"
            :key="player.id"
            class="score-preview-row"
          >
            <span class="score-preview-name">{{ player.name }}</span>
            <span
              class="score-preview-value"
              :class="{
                positive: scorePreview && scorePreview[player.id] > 0,
                negative: scorePreview && scorePreview[player.id] < 0,
              }"
            >
              <template v-if="scorePreview">{{ scorePreview[player.id] > 0 ? '+' : '' }}{{ scorePreview[player.id] }}</template>
              <template v-else>—</template>
            </span>
          </div>
        </div>
      </section>
    </div>

    <AppFooter
      :show-back="true"
      :back-label="isEditing ? 'Annuleren' : 'Terug'"
      continue-label="Bevestigen"
      :can-continue="canConfirm"
      @back="goBack"
      @continue="confirm"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useGameStore } from '../../stores/game'
import { useContractStore } from '../../stores/contract'
import { calculateScores } from '../../models/scoring'
import ScoreBar from '../shared/ScoreBar.vue'
import AppFooter from '../shared/AppFooter.vue'

const TRICKS_CONTRACTS = ['samen', 'alleen', 'troel', 'abondance']
const PER_PLAYER_CONTRACTS = ['kleine-miserie', 'piccolo', 'grote-miserie', 'open-miserie']

export default {
  name: 'EnterResult',
  components: { ScoreBar, AppFooter },

  data() {
    return {
      tricksTaken: null,
      playerResults: {},
      singleResult: null,
    }
  },

  created() {
    if (this.isEditing && this.lastRoundResult) {
      this.tricksTaken = this.lastRoundResult.tricksTaken ?? null
      this.singleResult = this.lastRoundResult.singleResult ?? null
      this.playerResults = { ...(this.lastRoundResult.playerResults || {}) }
    }
  },

  computed: {
    ...mapState(useGameStore, ['players', 'editingRoundNumber', 'lastRoundResult']),
    ...mapState(useContractStore, ['contractState']),

    isEditing() {
      return !!this.editingRoundNumber
    },

    resultMode() {
      if (!this.contractState) return null
      const type = this.contractState.type
      if (TRICKS_CONTRACTS.includes(type)) return 'tricks'
      if (PER_PLAYER_CONTRACTS.includes(type)) return 'per-player'
      return 'single'
    },

    canConfirm() {
      if (!this.contractState) return false
      switch (this.resultMode) {
        case 'tricks':
          return this.tricksTaken !== null
        case 'per-player':
          return this.contractState.playerIds.every(id => this.playerResults[id] !== undefined)
        case 'single':
          return this.singleResult !== null
        default:
          return false
      }
    },

    scorePreview() {
      if (!this.canConfirm) return null
      return calculateScores(
        this.contractState,
        {
          tricksTaken: this.tricksTaken,
          singleResult: this.singleResult,
          playerResults: this.playerResults,
        },
        this.players
      )
    },
  },

  methods: {
    ...mapActions(useGameStore, ['setView', 'recordRoundResult', 'cancelEditLastRound', 'updateLastRoundResult']),

    playerName(id) {
      const p = this.players.find(pl => pl.id === id)
      return p ? p.name : ''
    },

    setPlayerResult(id, value) {
      this.playerResults = { ...this.playerResults, [id]: value }
    },

    goBack() {
      if (this.isEditing) {
        this.cancelEditLastRound()
        return
      }
      this.setView('playing')
    },

    confirm() {
      if (!this.canConfirm || !this.scorePreview) return
      const resultData = {
        tricksTaken: this.tricksTaken,
        singleResult: this.singleResult,
        playerResults: { ...this.playerResults },
      }
      if (this.isEditing) {
        this.updateLastRoundResult(this.scorePreview, resultData)
        return
      }
      this.recordRoundResult(this.scorePreview, resultData)
    },
  },
}
</script>

<style scoped>
.enter-result {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-y: contain;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.contract-card {
  flex-shrink: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  background: #fff;
  border-radius: 12px;
  padding: 0.75rem 1.25rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}

.contract-name {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2c3e50;
}

.contract-details {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.contract-suit {
  font-size: 1.4rem;
  line-height: 1;
  color: #2c3e50;
}
.contract-suit.red { color: #c0392b; }

.contract-tricks {
  font-size: 0.9rem;
  font-weight: 600;
  color: #555;
}

.contract-players {
  display: flex;
  gap: 0.3rem;
  flex-wrap: wrap;
}

.contract-player {
  font-size: 0.75rem;
  font-weight: 600;
  color: #fff;
  background-color: #2c3e50;
  padding: 0.2em 0.6em;
  border-radius: 999px;
}

/* Sections — grow to fill available space */
.section {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.section-title {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #7a8a7a;
  padding-left: 0.25rem;
}

/* Tricks grid */
.tricks-grid {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-auto-rows: 1fr;
  gap: 0.4rem;
}

.tricks-chip {
  padding: 0;
  font-size: 1.1rem;
  font-family: inherit;
  font-weight: 700;
  border: 2px solid transparent;
  border-radius: 10px;
  background: #fff;
  color: #2c3e50;
  cursor: pointer;
  touch-action: manipulation;
  text-align: center;
  transition: border-color 0.15s, background-color 0.15s, box-shadow 0.15s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.tricks-chip.selected {
  border-color: #2c3e50;
  background-color: #2c3e50;
  color: #fff;
  box-shadow: 0 2px 8px rgba(44, 62, 80, 0.3);
}

.tricks-chip:active {
  transform: scale(0.97);
}

/* Per-player results */
.player-results {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.player-result-row {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #fff;
  border-radius: 10px;
  padding: 0.6rem 0.85rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.player-result-name {
  flex: 1;
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
}

.toggle-group {
  display: flex;
  gap: 0.3rem;
}

.toggle-btn {
  padding: 0.5rem 0.75rem;
  font-size: 0.85rem;
  font-family: inherit;
  font-weight: 600;
  border: 2px solid #ccc;
  border-radius: 8px;
  background: #fff;
  color: #888;
  cursor: pointer;
  touch-action: manipulation;
  transition: border-color 0.15s, background-color 0.15s, color 0.15s;
}

.toggle-btn.succeeded.active {
  border-color: #27ae60;
  background-color: #e6f6ed;
  color: #27ae60;
}

.toggle-btn.failed.active {
  border-color: #c0392b;
  background-color: #fdecea;
  color: #c0392b;
}

.toggle-btn:active {
  transform: scale(0.97);
}

/* Single result */
.single-result {
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}

.result-btn {
  padding: 0;
  font-size: 1.2rem;
  font-family: inherit;
  font-weight: 700;
  border: 2px solid #ccc;
  border-radius: 10px;
  background: #fff;
  color: #888;
  cursor: pointer;
  touch-action: manipulation;
  transition: border-color 0.15s, background-color 0.15s, color 0.15s;
}

.result-btn.succeeded.active {
  border-color: #27ae60;
  background-color: #e6f6ed;
  color: #27ae60;
}

.result-btn.failed.active {
  border-color: #c0392b;
  background-color: #fdecea;
  color: #c0392b;
}

.result-btn:active {
  transform: scale(0.97);
}

/* Score preview — fixed size, doesn't compete with input */
.section:has(.score-preview) {
  flex: 0 0 auto;
}

.score-preview {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.score-preview-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border-radius: 8px;
  padding: 0.5rem 0.85rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.score-preview-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: #2c3e50;
}

.score-preview-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: #999;
  min-width: 3rem;
  text-align: right;
}

.score-preview-value.positive {
  color: #27ae60;
}

.score-preview-value.negative {
  color: #c0392b;
}

@media (hover: hover) {
  .tricks-chip:hover { border-color: #7a9a7a; }
  .toggle-btn:hover { border-color: #999; }
  .result-btn:hover { border-color: #999; }
}
</style>
